/**
 * Suitability Service
 *
 * Display layer over the shared suitability rules (`@repo/shared`
 * `checkProductSuitability`) — maps the core verdict's issue types to
 * human-readable messages for the custom-portfolio builder UI.
 */

import {
  checkProductSuitability as checkCoreSuitability,
  pickT,
  type SuitabilityIssueType,
} from "@repo/shared";
import type { FrozenSuitabilityVerdict, Translated } from "@repo/shared/types";
import type { ClientClassificationSnapshot, ProductAssessmentSnapshot } from "@/lib/types/flow";
import { T } from "./suitability-service.i18n";

/**
 * A single suitability issue with details
 */
export type SuitabilityIssue = {
  type: SuitabilityIssueType;
  message: string;
};

/**
 * Result of a product suitability check
 */
export type ProductSuitabilityResult = {
  productId: string;
  isSuitable: boolean;
  issues: SuitabilityIssue[];
};

/**
 * Product with suitability metadata needed for checking
 */
export type ProductWithSuitabilityMeta = {
  id: string;
  name: string;
  isin: string | null;
  ticker: string | null;
  dataset: string | null;
  orderAmountType: "amount" | "units";
  productTypeId: string | null;
  productTypeName?: string;
  minTimeHorizonId: string | null;
  minTimeHorizonOrder?: number;
  minTimeHorizonName?: string;
  minRiskLevelId: string | null;
  minRiskLevelOrder?: number;
  minRiskLevelName?: string;
  /** QL-10 — minimum goal risk score. Absent = not configured; 0 is a real score. */
  minRiskScore?: number;
  /** Minimum buy per event (QP-193). Only set when the collection enforces minimums. */
  minBuyAmount?: number | null;
  /** Preference-ordered wrapper syncKeys (index 0 = first choice); empty when
   * untagged or the collection has wrapper types disabled. */
  wrapperSyncKeys?: string[];
};

/**
 * Context for suitability checking
 */
export type SuitabilityContext = {
  // Client classification
  classification: ClientClassificationSnapshot | null;

  // Product assessment
  experience: ProductAssessmentSnapshot | null;

  // Goal risk assessment
  goalRiskLevelOrder?: number;
  goalRiskLevelName?: string;
  /** QL-10 — the goal's final risk score. Absent = no submitted risk assessment. */
  goalRiskScore?: number;
  goalTimeHorizonOrder?: number;
  goalTimeHorizonName?: string;
};

/** Build the display message for one core issue type. */
function issueMessage(
  type: SuitabilityIssueType,
  product: ProductWithSuitabilityMeta,
  context: SuitabilityContext,
  t: Translated<typeof T>,
): string {
  switch (type) {
    case "no_experience": {
      const entry = context.experience?.experiences.find(
        (exp) => exp.productTypeId === product.productTypeId,
      );
      const productTypeName =
        product.productTypeName || entry?.productTypeName || t.suitabilityThisProductType;

      return (
        (entry?.assessments?.length ?? 0) > 0
          ? t.suitabilityAssessmentNotPassed
          : t.suitabilityNoExperience
      )(productTypeName);
    }
    case "risk_too_low":
      return product.minRiskLevelName
        ? t.suitabilityRiskTooLowWithMin(product.minRiskLevelName)
        : t.suitabilityRiskTooLow;
    // QL-10 — the minimum is the score itself, so it is always available here.
    case "risk_score_too_low":
      return t.suitabilityRiskScoreTooLow(String(product.minRiskScore));
    case "time_horizon_too_short":
      return product.minTimeHorizonName
        ? t.suitabilityTimeHorizonTooShortWithMin(product.minTimeHorizonName)
        : t.suitabilityTimeHorizonTooShort;
  }
}

/**
 * Check if a product is suitable for a client based on their context —
 * shared-core verdict decorated with display messages.
 */
function checkProductSuitability(
  product: ProductWithSuitabilityMeta,
  context: SuitabilityContext,
  t: Translated<typeof T>,
): ProductSuitabilityResult {
  const core = checkCoreSuitability(product, context);

  return {
    productId: product.id,
    isSuitable: core.isSuitable,
    issues: core.issues.map((type) => ({
      type,
      message: issueMessage(type, product, context, t),
    })),
  };
}

/**
 * QL-707 — the display result of a verdict frozen at submit: the same issue
 * messages as the live check, built from the names frozen with it, so a product
 * that is gone from the collection still explains why it was not suitable.
 * `lang` is the language the issue messages are worded in.
 */
export function frozenSuitabilityResult(
  productId: string,
  verdict: FrozenSuitabilityVerdict,
  lang: string,
): ProductSuitabilityResult {
  const t = pickT(T, lang);
  const product = {
    productTypeName: verdict.detail.productTypeName,
    minRiskLevelName: verdict.detail.minRiskLevelName,
    minRiskScore: verdict.detail.minRiskScore,
    minTimeHorizonName: verdict.detail.minTimeHorizonName,
  } as ProductWithSuitabilityMeta;
  // Only the assessment-vs-declaration wording reads the context.
  const context = {
    experience: verdict.detail.assessmentFailed
      ? { experiences: [{ productTypeId: undefined, assessments: [{}] }] }
      : null,
  } as unknown as SuitabilityContext;
  return {
    productId,
    isSuitable: verdict.isSuitable,
    issues: verdict.issues.map((type) => ({
      type,
      message: issueMessage(type, product, context, t),
    })),
  };
}

/**
 * Check suitability for multiple products. `lang` is the language the issue
 * messages are worded in (inside a flow: the flow language).
 */
export function checkProductsSuitability(
  products: ProductWithSuitabilityMeta[],
  context: SuitabilityContext,
  lang: string,
): Map<string, ProductSuitabilityResult> {
  const t = pickT(T, lang);
  const results = new Map<string, ProductSuitabilityResult>();

  for (const product of products) {
    results.set(product.id, checkProductSuitability(product, context, t));
  }

  return results;
}

/**
 * Get a human-readable summary of suitability issues
 */
export function getSuitabilityIssueSummary(result: ProductSuitabilityResult): string {
  if (result.isSuitable) {
    return "Suitable";
  }

  return result.issues.map((issue) => issue.message).join("; ");
}
